import { IDBPDatabase, openDB } from 'idb';
import { Platform } from 'react-native';
import type lunr from 'lunr';
import {
  Config,
  CRMAccount,
  CRMAccountSyncCheckpoint,
  CRMAccountSyncStatus,
  CrmDBSchema,
  TableName,
  FullTextSearch,
  TableEnum,
  FormSettings,
} from './CRMIndexedDBTypes';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import { CRMAccountSettings } from '@alucio/aws-beacon-amplify/src/models';
import { SyncEntry, SYNC_ENTRY_STATUS } from './ISyncManifest';
import isEqual from 'lodash/isEqual';
import { decodeHTMLObject } from 'src/utils/stringHelpers';
import * as logger from 'src/utils/logger'
import { isStorageQuotaLimitReached } from 'src/utils/storageQuota';
import PWALoaded from 'src/state/global/PWALoaded';
import { VeevaVaultFormSetting } from './CRMVeevaVaultTypes';
import {
  CRM_ACCOUNT_SEARCH_CANDIDATE_LIMIT,
  CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE,
  CRM_ACCOUNT_SEARCH_INDEX_CHUNK_SIZE,
  CRM_ACCOUNT_SEARCH_RESULT_LIMIT,
  isIOSCRMAccountLocalSearch,
} from './CRMAccountSearchIndex';
import { CRMAccountSearchDB } from './CRMAccountSearchDB';
import { CRMAccountSearchSyncer } from './CRMAccountSearchSyncer';

const loadLunr = async (): Promise<typeof lunr> => {
  // Keep Lunr out of the PWA attendee-search path; large CRM account datasets can make the full index expensive in memory.
  const lunrModule = await import('lunr');
  return (lunrModule.default ?? lunrModule) as typeof lunr;
};

export {
  buildCRMAccountSearchRows,
  isIOSCRMAccountLocalSearch,
  normalizeCRMAccountSearchToken,
  tokenizeCRMAccountSearchText,
} from './CRMAccountSearchIndex';

type dbItem =
  | Config
  | CRMAccount
  | CRMAccountSyncCheckpoint
  | FullTextSearch
  | SyncEntry
  | FormSettings
  | VeevaVaultFormSetting;
type dbItemArray = Array<dbItem>;
type dbItemStore = dbItem | dbItemArray;

export class IndexDbCrm {
  readonly DB_NAME = 'CRMIndexDb';
  readonly VERSION = 2;
  readonly DEBUG = false;
  // if we can cache the full text search index we can persist it to the DB
  readonly DISABLE_FULL_TEXT_SEARCH_CACHE = false;
  public index: lunr.Index | null = null;
  private config: CRMAccountSettings | null = null;
  private enableIOSLocalAccountSearch = false;
  private accountSyncStatus: CRMAccountSyncStatus | undefined;
  private accountSearchDB = new CRMAccountSearchDB();
  private previousFormRecordsResponse: (FormSettings | VeevaVaultFormSetting)[] | undefined;
  private accountSearchSyncer: CRMAccountSearchSyncer | null = null;

  private canHydrateSearch = () => {
    return !this.DISABLE_FULL_TEXT_SEARCH_CACHE
  }

  setEnableIOSLocalAccountSearch = (enabled: boolean) => {
    this.enableIOSLocalAccountSearch = enabled;
  };

  syncIOSLocalAccountSearchFeatureFlag = () => {
    // Keep Redux loading lazy here to avoid a CRM module cycle during isolated translator tests.
    const { store } = require('src/state/redux') as Pick<typeof import('src/state/redux'), 'store'>;
    const { selCurrentUserPermissions } = require('src/state/redux/selector/permissions') as Pick<
      typeof import('src/state/redux/selector/permissions'),
      'selCurrentUserPermissions'
    >;
    const featureFlags = selCurrentUserPermissions(store.getState());
    this.setEnableIOSLocalAccountSearch(featureFlags.BEAC_8117_crm_ios_local_account_search);
  }

  // Web and PWA always use IndexedDB tokens; the iOS tablet fallback remains feature-flagged.
  private shouldIndexAccountSearchRows = () => (
    Platform.OS === 'web' || Boolean(PWALoaded.isPWA) || (
      this.enableIOSLocalAccountSearch && isIOSCRMAccountLocalSearch()
    )
  );

  isAccountSearchIndexEnabled = () => this.shouldIndexAccountSearchRows();

  getAccountSearchAvailabilityInfo = () => ({
    isAccountSearchIndexEnabled: this.isAccountSearchIndexEnabled(),
    isFeatureFlagEnabled: this.enableIOSLocalAccountSearch,
    isPWA: Boolean(PWALoaded.isPWA),
  });

  getAccountSearchDebugInfo = async () => {
    const indexedTokenCount = await this.accountSearchDB.getIndexedTokenCount();
    const backfillMarker = indexedTokenCount
      ? await this.accountSearchDB.getBackfillMarker()
      : null;
    const accountCount = await this.getAccountCount();

    return {
      accountCount,
      backfillMarker,
      indexedTokenCount,
      isAccountSearchIndexEnabled: this.isAccountSearchIndexEnabled(),
    };
  };

  constructor(hydrateConfig = true) {
    // Hydrates account display config when CRM data already exists locally at app start.
    if (hydrateConfig) {
      this.getConfig().then((config) => {
        this.config = config || null;
      });
    }
  }

  private getProperties = (item: any) => {
    return Object.entries(item).reduce<Record<string, any>>((acc, [key, value]) => {
      if (!(
        (isArray(value) ||
          isObject(value)) &&
        !isEmpty(value)
      )) { acc[key] = value; }
      return acc;
    }, {});
  }

  private async calculateSearchIndex() {
    const data = await this.getAll<CRMAccount>(TableEnum.ACCOUNT);
    const getProperties = this.getProperties;

    if (!data.length) { return null; }

    const keys = Object.keys(getProperties(data[0]))

    const lunrModule = await loadLunr();
    return lunrModule(function () {
      this.ref('id');
      const FIELDS_TO_OMIT = ['id', 'personcontactid', 'ispersonaccount', 'lastmodifieddate'];
      keys.forEach((key) => {
        const shouldBeOmitted = FIELDS_TO_OMIT.includes(key.toLowerCase());
        if (!shouldBeOmitted) {
          this.field(key);
        }
      });

      data.forEach((account) => {
        this.add(getProperties(account));
      })
    });
  }

  private async hydrateSearchIndex() {
    const cachedIndex = await this.getById<FullTextSearch>(TableEnum.FULL_TEXT_SEARCH, '1')

    if (cachedIndex) {
      const lunrModule = await loadLunr();
      const { value } = cachedIndex;
      const searchIndex = lunrModule.Index.load(JSON.parse(value));
      await this.cacheSearchIndex(searchIndex)
      return searchIndex
    }

    return null
  }

  private cacheSearchIndex = async (searchIndex: lunr.Index | null) => {
    if (!searchIndex) { return }

    const serialized = JSON.stringify(searchIndex.toJSON())
    await this.upsertItem(
      TableEnum.FULL_TEXT_SEARCH,
      { id: '1', value: serialized },
    )
  }

  private setSearchIndex = (searchIndex: lunr.Index | null) => {
    this.index = searchIndex
  }

  initializeSearchIndex = async () => {
    if (this.shouldIndexAccountSearchRows()) {
      // Token search is the active path, so avoid hydrating or building the full Lunr index.
      this.setSearchIndex(null);
      return;
    }

    let searchIndex: lunr.Index | null = null
    if (this.canHydrateSearch()) {
      searchIndex = await this.hydrateSearchIndex()
    }

    if (!searchIndex) {
      searchIndex = await this.calculateSearchIndex()
    }

    this.setSearchIndex(searchIndex)
  }

  searchIndex = async (query: string): Promise<lunr.Index.Result[]> => {
    if (this.shouldIndexAccountSearchRows()) {
      return [];
    }

    if (!this.index) {
      await this.initializeSearchIndex();
    }

    return this.index?.search(query) ?? [];
  }

  private refreshSearchIndex = async () => {
    if (this.shouldIndexAccountSearchRows()) {
      this.setSearchIndex(null);
      return;
    }

    const updatedIndex = await this.calculateSearchIndex()

    if (this.canHydrateSearch() && updatedIndex) {
      await this.cacheSearchIndex(updatedIndex)
    }

    this.setSearchIndex(updatedIndex)
  }

  private clearCachedSearchIndex = async () => {
    this.setSearchIndex(null);
    const db = await this.getDB();
    const tx = db.transaction(TableEnum.FULL_TEXT_SEARCH, 'readwrite');
    await tx.store.delete('1');
    await tx.done;
  }

  // get a singleton of the openIDB function
  private openIDB = (() => {
    let openIDB: Promise<IDBPDatabase<CrmDBSchema>> | null = null;
    return () => {
      if (!openIDB) {
        openIDB = openDB<CrmDBSchema>(this.DB_NAME, this.VERSION, {
          upgrade(db) {
            if (!db.objectStoreNames.contains(TableEnum.ACCOUNT)) {
              db.createObjectStore(TableEnum.ACCOUNT, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(TableEnum.ACCOUNT_SYNC)) {
              db.createObjectStore(TableEnum.ACCOUNT_SYNC, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(TableEnum.CONFIG_OBJECT)) {
              db.createObjectStore(TableEnum.CONFIG_OBJECT, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(TableEnum.FULL_TEXT_SEARCH)) {
              db.createObjectStore(TableEnum.FULL_TEXT_SEARCH, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(TableEnum.FORM_SETTINGS)) {
              db.createObjectStore(TableEnum.FORM_SETTINGS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(TableEnum.FORM_MANIFEST)) {
              db.createObjectStore(TableEnum.FORM_MANIFEST, { keyPath: 'id' });
            }
          },
        });
      }
      return openIDB;
    };
  })();

  private getDB = async () => {
    const db = await this.openIDB();

    if (!db) {
      throw new Error('DB not initialized');
    }

    return db;
  };

  private getTx = async (tableName: TableName) => {
    const db = await this.getDB();
    const tx = db.transaction(tableName, 'readwrite');
    if (this.DEBUG) {
      tx.oncomplete = () => {
        logger.CRMMachine.warn('Transaction completed');
      };
      tx.onerror = (event) => {
        console.error('Transaction error: ', event);
      };
      tx.onabort = (event) => {
        console.warn('Transaction aborted: ', event);
      };
    }
    return tx;
  };

  private upsertItem = async <T>(
    tableName: TableName,
    item: dbItem,
  ): Promise<T | null> => {
    const tx = await this.getTx(tableName);
    try {
      await tx.store.put(item);
      return this.getById(tableName, item.id);
    } catch (e) {
      console.error('Error upserting item', e);
      return null;
    }
  };

  private setConfigFromItem = (item: dbItem): CRMAccountSettings | null => {
    if (!('configBase64' in item) || typeof item.configBase64 !== 'string') {
      return null;
    }

    try {
      const decoded = atob(item.configBase64);
      const parsed = JSON.parse(decoded);
      this.config = parsed.accountsSettings || null;
      return this.config;
    } catch (e) {
      logger.CRMMachine.warn('Unable to parse CRM config object', { error: e });
      return null;
    }
  }

  private getAccountConfig = async (): Promise<CRMAccountSettings | null> => {
    return await this.getConfig();
  }

  private readAccountSearchBackfillChunk = async (
    lastAccountId?: string,
  ): Promise<{
      accounts: CRMAccount[];
      lastAccountId?: string;
      hasMoreAccounts: boolean;
    }> => {
    const db = await this.getDB();
    const tx = db.transaction(TableEnum.ACCOUNT, 'readonly');
    const accountStore = tx.objectStore(TableEnum.ACCOUNT);
    const range = lastAccountId ? IDBKeyRange.lowerBound(lastAccountId, true) : undefined;
    let cursor = await accountStore.openCursor(range);
    const accounts: CRMAccount[] = [];
    let nextLastAccountId = lastAccountId;

    while (cursor && accounts.length < CRM_ACCOUNT_SEARCH_INDEX_CHUNK_SIZE) {
      accounts.push(cursor.value);
      nextLastAccountId = String(cursor.key);
      cursor = await cursor.continue();
    }

    await tx.done;

    return {
      accounts,
      lastAccountId: nextLastAccountId,
      hasMoreAccounts: Boolean(cursor),
    };
  }

  getAccountCount = async (): Promise<number> => {
    const db = await this.getDB();
    const tx = db.transaction(TableEnum.ACCOUNT, 'readonly');
    const count = await tx.store.count();
    await tx.done;
    return count;
  }

  getAccountSyncCheckpoint = async (
    crmUserId: string,
  ): Promise<CRMAccountSyncCheckpoint | undefined> => {
    const checkpoint = await this.getById<CRMAccountSyncCheckpoint>(TableEnum.ACCOUNT_SYNC, crmUserId);
    this.accountSyncStatus = checkpoint?.status;
    return checkpoint;
  };

  private indexAccountSyncPage = async (
    accounts: CRMAccount[],
    checkpoint: Omit<CRMAccountSyncCheckpoint, 'id'>,
  ): Promise<void> => {
    if (
      !this.shouldIndexAccountSearchRows() ||
      checkpoint.status === CRMAccountSyncStatus.ERROR
    ) {
      return;
    }

    const config = await this.getAccountConfig();
    await this.accountSearchDB.putAccountSearchRows(accounts, config);
    await this.accountSearchDB.setBackfillMarker({
      status: checkpoint.status === CRMAccountSyncStatus.COMPLETED
        ? CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE
        : 'in_progress',
      lastAccountId: accounts.length ? accounts[accounts.length - 1].id : undefined,
      processedCount: checkpoint.syncedCount,
      accountCount: await this.getAccountCount(),
      configHash: this.accountSearchDB.getBackfillConfigHash(config),
    });
  };

  persistAccountSyncPage = async (
    crmUserId: string,
    accounts: CRMAccount[],
    checkpoint: Omit<CRMAccountSyncCheckpoint, 'id'>,
  ): Promise<void> => {
    const db = await this.getDB();
    const tx = db.transaction([TableEnum.ACCOUNT, TableEnum.ACCOUNT_SYNC], 'readwrite');
    const accountStore = tx.objectStore(TableEnum.ACCOUNT);
    const checkpointStore = tx.objectStore(TableEnum.ACCOUNT_SYNC);

    await Promise.all(accounts.map(account => accountStore.put(account)));
    await checkpointStore.put({ id: crmUserId, ...checkpoint });
    await tx.done;
    this.accountSyncStatus = checkpoint.status;

    await this.indexAccountSyncPage(accounts, checkpoint);
  };

  private hasIncompleteAccountSync = () => (
    this.accountSyncStatus === CRMAccountSyncStatus.SYNCING ||
    this.accountSyncStatus === CRMAccountSyncStatus.ERROR
  );

  private getAccountSearchSyncer = () => {
    if (!this.accountSearchSyncer) {
      this.accountSearchSyncer = new CRMAccountSearchSyncer({
        accountSearchDB: this.accountSearchDB,
        getAccountConfig: this.getAccountConfig,
        getAccountCount: this.getAccountCount,
        readAccountSearchBackfillChunk: this.readAccountSearchBackfillChunk,
        isStorageQuotaLimitReached,
      });
    }

    return this.accountSearchSyncer;
  };

  /*
    * Upsert data into the database
    * @param tableName - the name of the table to upsert into
    * @param data - the data to upsert
    * @param syncFullTextSearch - whether to sync the full text search index
  */
  upsert = async <T>(tableName: TableName, data: dbItemStore) => {
    const decodedData = decodeHTMLObject(data)

    if (!Array.isArray(decodedData)) {
      if (tableName === TableEnum.CONFIG_OBJECT) {
        this.setConfigFromItem(decodedData);
      }
      const result = await this.upsertItem<T>(tableName, decodedData) as T;
      if (tableName === TableEnum.ACCOUNT) {
        await this.clearCachedSearchIndex();
      }
      return result;
    }

    if (tableName === TableEnum.CONFIG_OBJECT) {
      decodedData.forEach(item => this.setConfigFromItem(item));
    }

    if (Array.isArray(decodedData)) {
      await this.upsertArray(tableName, decodedData)
    }

    const shouldRefreshSearchIndex = [
      TableEnum.ACCOUNT.toString(),
      TableEnum.CONFIG_OBJECT.toString(),
      TableEnum.FORM_SETTINGS.toString(),
      TableEnum.FORM_MANIFEST.toString(),
    ].includes(tableName)

    if (shouldRefreshSearchIndex && this.shouldIndexAccountSearchRows()) {
      await this.clearCachedSearchIndex();
      return;
    }

    if (shouldRefreshSearchIndex) { this.refreshSearchIndex() }
  };

  upsertArray = async (
    tableName: TableName,
    data: dbItemStore,
  ): Promise<void> => {
    if (!Array.isArray(data)) throw new Error('Data must be an array');

    const db = await this.openIDB();
    const tx = db.transaction(tableName, 'readwrite');
    const store = tx.objectStore(tableName);

    for (let i = 0; i < data.length; i += 1000) {
      const chunk = data.slice(i, i + 1000);
      await Promise.all(chunk.map(item => store.put(item)));
    }

    await tx.done;
  };

  getAll = async <T>(tableName: TableName): Promise<T[]> => {
    const tx = await this.getTx(tableName);
    const items = tx.store.getAll();
    return (items as unknown) as Promise<T[]>;
  };

  getFormSettingsRaw = async (): Promise<(FormSettings | VeevaVaultFormSetting)[]> => {
    const response = await this.getAll<FormSettings | VeevaVaultFormSetting>(TableEnum.FORM_SETTINGS)

    if (!isEqual(response, this.previousFormRecordsResponse)) {
      // THIS RESPONSE IS STORED WITHIN THE CLASS TO AVOID THE SELECTOR
      // TO CONSIDER IT AS A NEW OBJECT AND TRANSLATE IT AGAIN
      this.previousFormRecordsResponse = response;
    }
    return this.previousFormRecordsResponse || response;
  };

  getById = async <T>(tableName: TableName, id: string): Promise<T> => {
    const tx = await this.getTx(tableName);
    const item = tx.store.get(id);
    return (item as unknown) as T;
  };

  clearTable = async (tableName: TableName) => {
    const db = await this.getDB();
    await db.clear(tableName);
  }

  // purge all data from the database
  clearDB = async () => {
    const db = await this.getDB();
    this.config = null;
    this.index = null;
    this.accountSyncStatus = undefined;
    this.previousFormRecordsResponse = undefined;
    await Promise.all([
      db.clear(TableEnum.ACCOUNT),
      db.clear(TableEnum.ACCOUNT_SYNC),
      db.clear(TableEnum.CONFIG_OBJECT),
      db.clear(TableEnum.FULL_TEXT_SEARCH),
      db.clear(TableEnum.FORM_SETTINGS),
      db.clear(TableEnum.FORM_MANIFEST),
      this.accountSearchDB.clearIndex(),

    ]);
  };

  getConfig = async (): Promise<CRMAccountSettings | null> => {
    if (this.config) {
      return this.config;
    }

    const config = await this.getAll<Config>(TableEnum.CONFIG_OBJECT);

    if (config.length) {
      return this.setConfigFromItem(config[0]);
    }

    return null;
  };

  // filter index db by multiple indexes
  filterById = async (tableName: TableName, keys: string[]) => {
    const tx = await this.getTx(tableName);
    const items = keys.map((key) => tx.store.get(key));

    if (!items.length) {
      return [];
    }

    return Promise.all(items) as Promise<CRMAccount[]>;
  }

  syncAccountSearchIndex = async (options: { forceRebuild?: boolean } = {}): Promise<void> => {
    if (!this.shouldIndexAccountSearchRows()) {
      return;
    }

    await this.getAccountSearchSyncer().sync(options);
  }

  searchAccountsByTokenPrefix = async (
    searchText: string | string[],
    filterFn?: (value: CRMAccount, index: number, array: CRMAccount[]) => boolean,
  ): Promise<CRMAccount[]> => {
    if (!this.hasIncompleteAccountSync()) {
      await this.syncAccountSearchIndex();
    }

    const accountIds = await this.accountSearchDB.searchAccountIdsByTokenPrefixes(searchText);

    if (!accountIds.length) {
      return [];
    }

    const accounts: CRMAccount[] = [];
    const config = await this.getAccountConfig();

    for (
      let i = 0;
      i < accountIds.length && accounts.length < CRM_ACCOUNT_SEARCH_RESULT_LIMIT;
      i += CRM_ACCOUNT_SEARCH_CANDIDATE_LIMIT
    ) {
      const candidateIds = accountIds.slice(i, i + CRM_ACCOUNT_SEARCH_CANDIDATE_LIMIT);
      let candidateAccounts = (await this.filterById(TableEnum.ACCOUNT, candidateIds))
        .filter(Boolean)
        .filter(account => this.accountSearchDB.doesAccountMatchSearch(account, config, searchText));

      if (filterFn) {
        candidateAccounts = candidateAccounts.filter(filterFn);
      }

      accounts.push(...candidateAccounts);
    }

    return accounts.slice(0, CRM_ACCOUNT_SEARCH_RESULT_LIMIT);
  }

  addSyncEntry = async (entry: SyncEntry) => {
    // check if entry already exists
    const entryExist = await this.getById<SyncEntry>('FORM_MANIFEST', entry.id)

    if (!entryExist) {
      entry.firstSynced = new Date().getTime()
    }
    else {
      entry.firstSynced = entryExist.firstSynced || new Date().getTime()
    }

    return await this.upsert<SyncEntry>('FORM_MANIFEST', entry) as SyncEntry
  }

  getEntryByStatus = async (status: SYNC_ENTRY_STATUS) => {
    const SyncEntries = await this.getAll<SyncEntry>('FORM_MANIFEST')
    return SyncEntries.filter((entry) => entry.status === status)
  }
}

export const Singleton = new Proxy(new IndexDbCrm(), {
  get: (target, prop) => {
    try {
      if (target[prop] !== undefined) {
        return target[prop]
      }

      // check if prop is an string
      if (typeof prop === 'string') {
        console.error(`IndexDbCrm: ${prop} is not a valid method`)
      }
    }
    catch (e) {
      console.error(e)
    }
  },
})
